'use client'

import Link from 'next/link'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

import { AvatarCard, AvatarCardSkeleton } from '@/components/ui/avatar-card'

interface AvatarCardListProps extends React.ComponentPropsWithoutRef<'div'> {
  list: any[]
  childrenAs?: 'link' | 'button'
  linkAs?: 'onboard-branch' | 'onboard-account'
  branch?: string
}

const variants = {
  animate: {
    opacity: 1,
    marginTop: 0,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

export const AvatarCardList = ({
  list,
  childrenAs,
  linkAs,
  className,
  branch,
}: AvatarCardListProps) => {
  return (
    <motion.div
      variants={variants}
      animate="animate"
      className={cn('p-1', className)}
    >
      {(list || [])?.map((item) => (
        <Card
          key={item?.id}
          item={item}
          childrenAs={childrenAs}
          linkAs={linkAs}
          branch={branch}
        />
      ))}
    </motion.div>
  )
}

const Card = ({
  item,
  childrenAs,
  linkAs,
  branch,
}: {
  item: any
  childrenAs?: string
  linkAs?: string
  branch?: string
}) => {
  const generateLink = (item: { id: any }, linkAs: string | undefined) => {
    switch (linkAs) {
      case 'onboard-branch':
        return `/onboard/${item?.id}`
      case 'onboard-account':
        return `/onboard/${branch}/pin/${item?.id}`
      default:
        throw new Error(`Invalid linkAs value: ${linkAs}`)
    }
  }

  if (childrenAs === 'link') {
    const link = generateLink(item, linkAs)

    return (
      <Link
        key={item?.id}
        href={link}
        passHref
        className="group rounded-lg border border-gray-100 border-transparent focus:border focus:dark:border-gray-700"
      >
        <AvatarCard
          variants={variants}
          shortCode={item?.code?.substring(0, 2)}
          label={item?.name}
          actionLabel="Select"
          className="mt-2 opacity-0"
        />
      </Link>
    )
  }

  return (
    <AvatarCard
      variants={variants}
      shortCode={item?.short_code}
      label={item?.name}
      actionLabel="Select"
      className="mt-2 opacity-0"
    />
  )
}

export const AvatarCardListSkeleton = () => {
  return (
    <motion.div
      variants={variants}
      animate="animate"
      className="flex w-full flex-col gap-2 py-8 opacity-100 md:px-4"
    >
      <AvatarCardSkeleton variants={variants} className="opacity-100" />
      <AvatarCardSkeleton variants={variants} className="opacity-100" />
      <AvatarCardSkeleton variants={variants} className="opacity-100" />
    </motion.div>
  )
}
